import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ExternalLink } from '../../components';

import css from './SharePanel.module.css';

const IconFacebook = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const IconX = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const IconWhatsApp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const IconEmail = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const IconCopy = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
  </svg>
);

/**
 * SharePanel — share a listing URL to social platforms.
 *
 * @param {string} url - full URL to share (e.g. https://marketplace.com/l/listing-slug/uuid)
 * @param {string} title - listing title for share text
 * @param {string} className
 */
const SharePanel = props => {
  const { url, title, className, rootClassName } = props;
  const intl = useIntl();
  const inputRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const shareText = title
    ? intl.formatMessage({ id: 'SharePanel.shareText' }, { title })
    : intl.formatMessage({ id: 'SharePanel.shareTextDefault' });

  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(shareText);

  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  const xHref = `https://twitter.com/share?text=${encodedText}&url=${encodedUrl}`;
  const whatsappHref = `https://wa.me/?text=${encodedText}+${encodedUrl}`;
  const emailHref = `mailto:?subject=${encodedText}&body=${encodedUrl}`;

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => setCopied(true));
    } else if (inputRef.current) {
      inputRef.current.select();
      document.execCommand('copy');
      setCopied(true);
    }
  };

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <p className={css.title}>
        <FormattedMessage id="SharePanel.title" />
      </p>

      <div className={css.buttons}>
        <ExternalLink href={facebookHref} className={css.btn} title="Facebook">
          <IconFacebook />
          <span className={css.btnLabel}>Facebook</span>
        </ExternalLink>

        <ExternalLink href={xHref} className={css.btn} title="X / Twitter">
          <IconX />
          <span className={css.btnLabel}>X</span>
        </ExternalLink>

        <ExternalLink href={whatsappHref} className={css.btn} title="WhatsApp">
          <IconWhatsApp />
          <span className={css.btnLabel}>WhatsApp</span>
        </ExternalLink>

        <ExternalLink href={emailHref} className={css.btn} title="Email">
          <IconEmail />
          <span className={css.btnLabel}><FormattedMessage id="SharePanel.email" /></span>
        </ExternalLink>

        <button className={css.btn} onClick={handleCopy} type="button" title="Copy link">
          <IconCopy />
          <span className={css.btnLabel}>
            {copied
              ? <FormattedMessage id="SharePanel.copied" />
              : <FormattedMessage id="SharePanel.copyLink" />}
          </span>
        </button>
      </div>

      {/* Fallback hidden input for older browsers */}
      <input
        ref={inputRef}
        className={css.hiddenInput}
        value={url}
        readOnly
        aria-hidden="true"
      />
    </div>
  );
};

export default SharePanel;
